import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/layout/Header";
import { Play, Calendar, Infinity as InfinityIcon, BarChart3, Lock, Sparkles } from "lucide-react";

const MiniBoard = () => {
  // decorative on-brand mini board
  const cells = [
    ["4", "b", "1", "9"],
    ["5", "6", "b", "0"],
    ["b", "8", "7", "4"],
    ["2", "0", "b", "b"],
  ];
  return (
    <div
      className="bg-ink p-1 rounded-lg border-2 border-ink"
      style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "3px" }}
    >
      {cells.flat().map((v, i) =>
        v === "b" ? (
          <div key={i} className="aspect-square bg-ink rounded-[2px]" />
        ) : (
          <div
            key={i}
            className="aspect-square bg-white rounded-[3px] grid place-items-center font-mono font-extrabold text-sm sm:text-base text-ink"
          >
            {v}
          </div>
        )
      )}
    </div>
  );
};

const DIFFS = ["easy", "medium", "hard"];

export default function Home() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [difficulty, setDifficulty] = useState("medium");

  const start = (mode) => navigate(`/play?mode=${mode}&difficulty=${difficulty}`);

  const comingSoon = [
    { id: "word-puzzle", name: t("nav_home") === "Home" ? "Word Puzzle" : "Kelime Bulmaca", color: "#FFC043" },
    { id: "memory-match", name: t("nav_home") === "Home" ? "Memory Match" : "Hafıza Oyunu", color: "#4CAF50" },
    { id: "brain-teaser", name: t("nav_home") === "Home" ? "Brain Teaser" : "Zeka Bulmaca", color: "#FF5A5F" },
  ];

  return (
    <div className="min-h-screen bg-world">
      <Header />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border-2 border-ink bg-sunny text-ink text-xs font-black font-display shadow-brutal-sm mb-5">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} /> {t("featured")}
          </span>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
            {t("brand")}
          </h1>
          <p className="mt-3 text-lg font-bold text-ink/70 font-display">{t("hero_tagline")}</p>
          <p className="mt-4 text-base font-medium text-ink/60 leading-relaxed max-w-xl">{t("hero_desc")}</p>
        </motion.div>
      </section>

      {/* Featured game */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid md:grid-cols-12 gap-6 items-stretch"
        >
          <div
            data-testid="featured-game-card"
            className="md:col-span-7 bg-white border-2 border-ink rounded-2xl shadow-brutal-lg p-6 sm:p-8"
          >
            <div className="flex items-start gap-5">
              <div className="w-24 sm:w-28 shrink-0">
                <MiniBoard />
              </div>
              <div className="min-w-0">
                <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
                  Number Placement Classic
                </h2>
                <p className="mt-2 text-sm font-medium text-ink/60 leading-relaxed">
                  {t("nav_home") === "Home"
                    ? "Drop number blocks into gaps of the right length and position. Crossing digits must match."
                    : "Sayı bloklarını doğru uzunluk ve konumdaki boşluklara yerleştir. Kesişen rakamlar uyumlu olmalı."}
                </p>
              </div>
            </div>

            {/* Difficulty */}
            <div className="mt-6">
              <div className="text-xs font-black uppercase tracking-[0.2em] text-ink/50 font-display mb-2">
                {t("choose_difficulty")}
              </div>
              <div className="inline-flex rounded-lg border-2 border-ink overflow-hidden shadow-brutal-sm">
                {DIFFS.map((d) => (
                  <button
                    key={d}
                    data-testid={`difficulty-${d}`}
                    onClick={() => setDifficulty(d)}
                    className={`px-4 sm:px-5 py-2 text-sm font-black font-display transition-colors border-r-2 border-ink last:border-0 ${
                      difficulty === d ? "bg-ink text-cream" : "bg-white text-ink hover:bg-black/5"
                    }`}
                  >
                    {t(d)}
                  </button>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="daily-game-btn"
                onClick={() => start("daily")}
                className="inline-flex items-center justify-center gap-2 flex-1 px-6 py-3.5 rounded-xl bg-coral text-white border-2 border-ink font-display font-bold shadow-brutal hover:shadow-brutal-lg hover:-translate-y-0.5 transition-all"
              >
                <Calendar className="w-5 h-5" strokeWidth={2.5} /> {t("daily_game")}
              </button>
              <button
                data-testid="endless-game-btn"
                onClick={() => start("endless")}
                className="inline-flex items-center justify-center gap-2 flex-1 px-6 py-3.5 rounded-xl bg-sunny text-ink border-2 border-ink font-display font-bold shadow-brutal hover:shadow-brutal-lg hover:-translate-y-0.5 transition-all"
              >
                <InfinityIcon className="w-5 h-5" strokeWidth={2.5} /> {t("endless_game")}
              </button>
            </div>
          </div>

          {/* How to play + stats */}
          <div className="md:col-span-5 flex flex-col gap-6">
            <div className="bg-ink text-cream border-2 border-ink rounded-2xl shadow-brutal-lg p-6 flex-1">
              <div className="flex items-center gap-2 mb-4">
                <Play className="w-5 h-5" strokeWidth={2.5} />
                <h3 className="font-display font-black text-xl">{t("how_to_play")}</h3>
              </div>
              <ol className="space-y-3">
                {["how_1", "how_2", "how_3", "how_4"].map((k, i) => (
                  <li key={k} className="flex gap-3 items-start">
                    <span className="grid place-items-center w-6 h-6 rounded-full bg-sunny text-ink text-xs font-black font-display shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-sm font-medium text-cream/80 leading-snug">{t(k)}</span>
                  </li>
                ))}
              </ol>
            </div>

            <button
              data-testid="home-stats-btn"
              onClick={() => navigate("/stats")}
              className="inline-flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-2xl bg-white text-ink border-2 border-ink font-display font-bold shadow-brutal hover:-translate-y-0.5 transition-transform"
            >
              <BarChart3 className="w-5 h-5" strokeWidth={2.5} /> {t("view_stats")}
            </button>
          </div>
        </motion.div>
      </section>

      {/* Coming soon */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="border-t-2 border-ink/10 pt-8">
          <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight">{t("more_games")}</h3>
          <p className="mt-1 text-sm font-medium text-ink/50 max-w-lg">{t("more_games_desc")}</p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {comingSoon.map((g) => (
              <div
                key={g.id}
                data-testid={`coming-soon-${g.id}`}
                className="relative bg-white/60 border-2 border-dashed border-ink/30 rounded-2xl p-6 overflow-hidden"
              >
                <div
                  className="w-12 h-12 rounded-lg border-2 border-ink/30 mb-4 grid place-items-center"
                  style={{ backgroundColor: `${g.color}33` }}
                >
                  <Lock className="w-5 h-5 text-ink/40" strokeWidth={2.5} />
                </div>
                <h4 className="font-display font-black text-lg text-ink/70">{g.name}</h4>
                <span className="mt-2 inline-block px-2.5 py-0.5 rounded-full bg-ink/10 text-ink/50 text-xs font-black font-display uppercase tracking-wider">
                  {t("coming_soon")}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
